import { useTranslation } from "react-i18next";
import type { EnterpriseAssetsV2SummaryResponse } from "../api/enterpriseClient";
import { formatAssetsV2Count } from "../utils";
import { AssetsV2MetricCard } from "./AssetsV2MetricCard";

export function AssetsV2MetricsGrid({
  summary,
}: {
  summary: EnterpriseAssetsV2SummaryResponse["summary"];
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const percentageNumberFormat = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const percentageFormat = new Intl.NumberFormat(locale, {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  return (
    <div className="grid grid-cols-1 gap-(--spacing-12) sm:grid-cols-3">
      <AssetsV2MetricCard
        icon="asset"
        iconClass="bg-primary/10 text-primary"
        label={t("enterprise.assetsV2.monitoredAssets")}
        value={formatAssetsV2Count(summary.monitoredAssetCount, locale)}
        description={t("enterprise.assetsV2.monitoredAssetsDetail", {
          facilities: formatAssetsV2Count(summary.facilityCount, locale),
          points: formatAssetsV2Count(summary.monitoringPointCount, locale),
        })}
      />
      <AssetsV2MetricCard
        icon="maintenance"
        iconClass="bg-accessibility-warning/10 text-accessibility-warning"
        label={t("enterprise.assetsV2.recommended")}
        value={formatAssetsV2Count(
          summary.maintenanceRequiredAssetCount,
          locale,
        )}
        description={t("enterprise.assetsV2.recommendedDetail", {
          percent: percentageNumberFormat.format(
            summary.maintenanceRequiredAssetPercentage,
          ),
          alerts: formatAssetsV2Count(
            summary.maintenanceRequiredAlertCount,
            locale,
          ),
        })}
      />
      <AssetsV2MetricCard
        icon="wifi"
        iconClass="bg-positive-container text-positive"
        label={t("enterprise.assetsV2.monitoringOnline")}
        value={
          summary.monitoringOnlinePercentage === null
            ? "—"
            : percentageFormat.format(summary.monitoringOnlinePercentage / 100)
        }
        description={t("enterprise.assetsV2.monitoringOnlineDetail", {
          assets: formatAssetsV2Count(
            summary.monitoringOfflineAssetCount,
            locale,
          ),
          hardware: formatAssetsV2Count(summary.offlineHardwareCount, locale),
        })}
      />
    </div>
  );
}
